import React, { useState, useEffect } from 'react';
import { post } from '../../../services/smartApiService';
import { AI_TRADER_URLS } from '../../../api/AiTraderUrls';

interface Briefing {
  available: boolean;
  message?: string;
  market_bias?: string;
  summary?: string;
  trading_strategy?: string;
  vix_outlook?: string;
  gap_info?: string;
  key_events?: string[];
  key_levels?: number[];
  risks?: string[];
  created_at?: string;
}

interface Props {
  briefing: Briefing | null;
  loading: boolean;
  onRefresh: () => void;
}

const biasStyle: Record<string, { color: string; bg: string; label: string }> = {
  strongly_bullish: { color: '#26a65b', bg: '#0d3320', label: '▲▲ Strongly Bullish' },
  bullish:          { color: '#2ecc71', bg: '#0d2a1a', label: '▲ Bullish' },
  neutral:          { color: '#f39c12', bg: '#2a1e0a', label: '— Neutral' },
  bearish:          { color: '#e67e22', bg: '#2a1608', label: '▼ Bearish' },
  strongly_bearish: { color: '#e74c3c', bg: '#3d0d0d', label: '▼▼ Strongly Bearish' },
};

const gapColor = (gap_info?: string) => {
  if (!gap_info) return '#888';
  if (gap_info.includes('gap_up') || (gap_info.includes('+') && !gap_info.includes('-0'))) return '#26a65b';
  if (gap_info.includes('gap_down')) return '#e74c3c';
  return '#f39c12';
};

export default function MorningBriefing({ briefing, loading, onRefresh }: Props) {
  const [generating, setGenerating] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const handleGenerate = () => {
    setGenerating(true);
    post(AI_TRADER_URLS.BRIEFING_GENERATE, {}, { showLoading: false }).subscribe({
      next: () => { setTimeout(() => { setGenerating(false); onRefresh(); }, 8000); },
      error: () => setGenerating(false),
    });
  };

  const fmtTime = (iso?: string) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-IN', {
      hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short',
    });
  };

  const bias = briefing?.market_bias || 'neutral';
  const biasInfo = biasStyle[bias] || biasStyle.neutral;

  return (
    <div style={{
      background: '#0a0a14', border: '1px solid #1a1a2a', borderRadius: 8,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px', borderBottom: '1px solid #1a1a2a',
        background: '#0d0d18',
      }}>
        <span style={{ color: '#555', fontSize: '0.65rem', fontWeight: 700, letterSpacing: 1 }}>
          MORNING BRIEFING
        </span>
        <span style={{ color: '#333', fontSize: '0.58rem' }}>
          Grok pre-market analysis · runs 8:30 AM IST
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          {briefing?.created_at && (
            <span style={{ color: '#333', fontSize: '0.58rem' }}>{fmtTime(briefing.created_at)}</span>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              background: 'transparent', border: '1px solid #1a3a5c',
              color: '#4fc3f7', fontSize: '0.6rem', padding: '2px 8px',
              borderRadius: 4, cursor: 'pointer', opacity: generating ? 0.6 : 1,
            }}
          >
            {generating ? '⟳ Generating...' : '↺ Generate Now'}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ padding: '1rem', color: '#444', fontSize: '0.7rem', textAlign: 'center' }}>
          Loading briefing...
        </div>
      ) : !briefing?.available ? (
        <div style={{
          padding: '12px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', gap: 12,
        }}>
          <span style={{ color: '#444', fontSize: '0.7rem' }}>
            {briefing?.message || 'No briefing available. Generates at 8:30 AM IST on trading days.'}
          </span>
        </div>
      ) : (
        <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* Bias + Gap row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{
              background: biasInfo.bg, border: `1px solid ${biasInfo.color}44`,
              borderRadius: 6, padding: '5px 12px',
            }}>
              <span style={{ color: biasInfo.color, fontSize: '0.78rem', fontWeight: 800 }}>
                {biasInfo.label}
              </span>
            </div>

            {briefing.gap_info && (
              <div style={{ background: '#111', border: '1px solid #222', borderRadius: 6, padding: '5px 12px' }}>
                <span style={{ color: '#888', fontSize: '0.6rem' }}>GAP: </span>
                <span style={{ color: gapColor(briefing.gap_info), fontSize: '0.72rem', fontWeight: 700 }}>
                  {briefing.gap_info.replace('Gap: ', '')}
                </span>
              </div>
            )}

            {briefing.vix_outlook && (
              <div style={{ background: '#111', border: '1px solid #222', borderRadius: 6, padding: '5px 12px', flex: 1 }}>
                <span style={{ color: '#888', fontSize: '0.6rem' }}>VIX: </span>
                <span style={{ color: '#f39c12', fontSize: '0.65rem' }}>{briefing.vix_outlook}</span>
              </div>
            )}
          </div>

          {/* Summary */}
          {briefing.summary && (
            <div style={{
              background: '#0d0d18', border: '1px solid #1a1a2a',
              borderRadius: 6, padding: '8px 10px',
              color: '#ccc', fontSize: '0.7rem', lineHeight: 1.6,
            }}>
              {briefing.summary}
            </div>
          )}

          {/* Trading Strategy */}
          {briefing.trading_strategy && (
            <div style={{
              background: `${biasInfo.bg}88`,
              border: `1px solid ${biasInfo.color}33`,
              borderRadius: 6, padding: '7px 10px',
              borderLeft: `3px solid ${biasInfo.color}`,
            }}>
              <div style={{ color: '#666', fontSize: '0.58rem', fontWeight: 700, marginBottom: 3 }}>
                TODAY'S STRATEGY
              </div>
              <div style={{ color: biasInfo.color, fontSize: '0.7rem', fontWeight: 600 }}>
                {briefing.trading_strategy}
              </div>
            </div>
          )}

          {/* Key events + levels + risks */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 8 }}>
            {/* Key events */}
            {briefing.key_events && briefing.key_events.length > 0 && (
              <div style={{ background: '#0d0d18', border: '1px solid #1a1a2a', borderRadius: 6, padding: '7px 10px' }}>
                <div style={{ color: '#555', fontSize: '0.58rem', fontWeight: 700, marginBottom: 5 }}>
                  KEY EVENTS TODAY
                </div>
                {briefing.key_events.slice(0, 4).map((ev, i) => (
                  <div key={i} style={{ color: '#aaa', fontSize: '0.65rem', marginBottom: 3, display: 'flex', gap: 5 }}>
                    <span style={{ color: '#f39c12' }}>•</span> {ev}
                  </div>
                ))}
              </div>
            )}

            {/* Risks */}
            {briefing.risks && briefing.risks.length > 0 && (
              <div style={{ background: '#0d0d18', border: '1px solid #3d0d0d', borderRadius: 6, padding: '7px 10px' }}>
                <div style={{ color: '#e74c3c', fontSize: '0.58rem', fontWeight: 700, marginBottom: 5 }}>
                  ⚠ RISKS TO WATCH
                </div>
                {briefing.risks.slice(0, 3).map((r, i) => (
                  <div key={i} style={{ color: '#c0392b', fontSize: '0.65rem', marginBottom: 3, display: 'flex', gap: 5 }}>
                    <span>•</span> {r}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Key levels */}
          {briefing.key_levels && briefing.key_levels.length > 0 && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ color: '#444', fontSize: '0.58rem', fontWeight: 700 }}>KEY LEVELS:</span>
              {briefing.key_levels.map((level, i) => (
                <span key={i} style={{
                  background: '#0d1a2a', border: '1px solid #1a3a5c',
                  color: '#4fc3f7', fontSize: '0.65rem', fontWeight: 700,
                  padding: '2px 8px', borderRadius: 4,
                }}>
                  {level.toLocaleString('en-IN')}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
